/**
 * Export lesbar machen (E21, 25.10.2026): Der Server liefert für
 * verschlüsselte Tabellen und Bilder nur Chiffrat — er kann sie nicht öffnen.
 * Das Gerät öffnet sie vor dem Teilen, mit dem Kontoschlüssel. Ist es nicht
 * entsperrt, bleibt das Chiffrat stehen, und der Export sagt es ausdrücklich
 * (`encryption.locked`) — eine stille Lücke sähe aus wie „keine Gedanken“.
 */

import { ENCRYPTED_TABLES, looksSealed } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import type { KraftwerkDatabase } from '../db/local.js';
import {
  fromB64,
  isSealed,
  keyForRow,
  knownKeys,
  openFile,
  openRow,
  readAccountKey,
} from './e2e.js';

type Row = Record<string, unknown>;

interface ExportShape {
  tables?: Record<string, Row[]>;
  /** Geteiltes (Läden, Orte …) — verschlüsselt mit dem Haushaltsschlüssel. */
  household?: Record<string, Row[]>;
  photos?: (Row & { jpegBase64?: string | null; payload?: string | null })[];
  recipeImages?: { recipeId: string; jpegBase64: string }[];
  [key: string]: unknown;
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function bytesOf(value: string): Uint8Array<ArrayBuffer> {
  return fromB64(value.replace(/\+/g, '-').replace(/\//g, '_'));
}

export async function openExport(
  data: ExportShape,
  database: KraftwerkDatabase = db,
): Promise<ExportShape & { encryption: { locked: boolean; stillSealed: number } }> {
  const key = await readAccountKey(database);
  let stillSealed = 0;
  const tables = { ...data.tables };
  // Der Haushaltsabschnitt (Läden, Orte) ist ebenso verschlüsselt (27.10.2026).
  const household = data.household ? { ...data.household } : undefined;

  for (const section of [tables, household]) {
    if (!section) continue;
    for (const table of ENCRYPTED_TABLES) {
      const rows = section[table];
      if (!rows) continue;
      section[table] = await Promise.all(
        rows.map(async (row) => {
          if (!isSealed(row)) return row;
          // Geteilte Zeilen mit dem Haushaltsschlüssel, private mit dem Kontoschlüssel.
          const { key: rowKey } = await keyForRow(row, database);
          if (!rowKey) {
            stillSealed += 1;
            return row;
          }
          return openRow(table, row, rowKey);
        }),
      );
    }
  }

  const photos = await Promise.all(
    (data.photos ?? []).map(async (photo) => {
      let next = photo;
      if (isSealed(photo)) {
        if (key) next = (await openRow('journal_photos', photo, key)) as typeof photo;
        else stillSealed += 1;
      }
      if (next.jpegBase64) {
        const bytes = bytesOf(next.jpegBase64);
        if (looksSealed(bytes)) {
          if (key) {
            next = { ...next, jpegBase64: base64(await openFile(bytes, key)) };
          } else stillSealed += 1;
        }
      }
      return next;
    }),
  );

  // Rezeptbilder: eigene mit dem Kontoschlüssel, geteilte Kopien mit dem des Haushalts.
  const keys = await knownKeys(database);
  const recipeImages = await Promise.all(
    (data.recipeImages ?? []).map(async (image) => {
      const bytes = bytesOf(image.jpegBase64);
      if (!looksSealed(bytes)) return image;
      for (const candidate of keys) {
        try {
          return { ...image, jpegBase64: base64(await openFile(bytes, candidate)) };
        } catch {
          // Nächster.
        }
      }
      stillSealed += 1;
      return image;
    }),
  );

  return {
    ...data,
    tables,
    ...(household ? { household } : {}),
    photos,
    ...(data.recipeImages ? { recipeImages } : {}),
    encryption: { locked: !key && stillSealed > 0, stillSealed },
  };
}
